// Einheiten & Mengen – eine einzige Quelle für Backend und Frontend.
// Basiseinheiten: ml (Volumen), g (Gewicht), Stück (Anzahl).

/** Einheiten, die in Rezepten/Listen ausgewählt werden dürfen. */
export const UNITS = [
  'Stück',
  'g',
  'kg',
  'ml',
  'l',
  'EL',
  'TL',
  'Prise',
  'Bund',
  'Dose',
  'Packung',
  'Becher',
  'Scheibe',
] as const;

/** Einheiten für Packungsgrößen (nur, was sich in eine Basiseinheit rechnen lässt). */
export const SIZE_UNITS = ['Stück', 'ml', 'l', 'g', 'kg'] as const;

export type Unit = (typeof UNITS)[number];
export type SizeUnit = (typeof SIZE_UNITS)[number];
export type BaseUnit = 'ml' | 'g' | 'Stück';

/** Einheiten, die „ganze Packung(en)" eines Produkts meinen. */
export const PACK_UNITS = new Set<string>(['Stück', 'Packung', 'Dose', 'Becher', 'Flasche']);

/** Mengen, die praktisch null sind – nie Fehlmenge, keine Abbuchung. */
export const NEGLIGIBLE_UNITS = new Set<string>(['Prise']);

interface UnitFactor {
  unit: BaseUnit;
  factor: number;
}

const UNIT_MAP: Record<string, UnitFactor> = {
  l: { unit: 'ml', factor: 1000 },
  liter: { unit: 'ml', factor: 1000 },
  ltr: { unit: 'ml', factor: 1000 },
  ml: { unit: 'ml', factor: 1 },
  cl: { unit: 'ml', factor: 10 },
  kg: { unit: 'g', factor: 1000 },
  g: { unit: 'g', factor: 1 },
  gr: { unit: 'g', factor: 1 },
  mg: { unit: 'g', factor: 0.001 },
  stück: { unit: 'Stück', factor: 1 },
  stk: { unit: 'Stück', factor: 1 },
  st: { unit: 'Stück', factor: 1 },
  x: { unit: 'Stück', factor: 1 },
  // Küchenmaße (Näherung, Volumen)
  el: { unit: 'ml', factor: 15 },
  tl: { unit: 'ml', factor: 5 },
};

export interface Quantity {
  value: number;
  unit: BaseUnit;
  multiplier?: number;
}

/**
 * Parst Mengenangaben („1,5 L", „500 g", „6 x 1.5 L", „1kg") in eine Basiseinheit.
 * Gibt `null` zurück, wenn nichts Verwertbares erkannt wurde.
 */
export function parseQuantity(input: string | number | null | undefined): Quantity | null {
  if (input === null || input === undefined || input === '') return null;
  const s = String(input).toLowerCase().replace(',', '.').trim();

  // Multiplikator „6 x 1.5 l" -> 6 * 1500 ml
  const mult = s.match(/^(\d+(?:\.\d+)?)\s*[x×*]\s*(.+)$/);
  if (mult?.[1] && mult[2]) {
    const n = parseFloat(mult[1]);
    const rest = parseQuantity(mult[2]);
    if (rest) return { value: n * rest.value, unit: rest.unit, multiplier: n };
  }

  const m = s.match(/(\d+(?:\.\d+)?)\s*(kg|mg|ml|cl|gr|g|ltr|liter|l|el|tl|stück|stk|st)\b/);
  if (m?.[1] && m[2]) {
    const value = parseFloat(m[1]);
    const u = UNIT_MAP[m[2]];
    if (u) return { value: value * u.factor, unit: u.unit };
  }

  // Nur Zahl -> Stück
  const num = s.match(/^(\d+(?:\.\d+)?)$/);
  if (num?.[1]) return { value: parseFloat(num[1]), unit: 'Stück' };

  return null;
}

/** Konvertiert eine Menge in eine Zieleinheit, falls kompatibel. Sonst `null`. */
export function convert(
  value: number,
  fromUnit: string | null | undefined,
  toUnit: string | null | undefined,
): number | null {
  if (!fromUnit || !toUnit) return null;
  if (fromUnit === toUnit) return value;
  const f = parseQuantity(`${value} ${fromUnit}`);
  const t = parseQuantity(`1 ${toUnit}`);
  if (f && t && f.unit === t.unit) return f.value / t.value;
  return null;
}

/** Größe (Zahl + Einheit) in die Basiseinheit umrechnen: l→ml, kg→g. */
export function toBaseSize(
  value: string | number,
  unit: string,
): { value: number | null; unit: BaseUnit | null } {
  const n = Number(String(value).replace(',', '.'));
  if (!Number.isFinite(n) || !unit) return { value: null, unit: null };
  const map: Record<string, [number, BaseUnit]> = {
    l: [n * 1000, 'ml'],
    ml: [n, 'ml'],
    kg: [n * 1000, 'g'],
    g: [n, 'g'],
    Stück: [n, 'Stück'],
  };
  const c = map[unit];
  return c ? { value: c[0], unit: c[1] } : { value: n, unit: unit as BaseUnit };
}

/**
 * Basiseinheit zurück in eine anzeigefreundliche (Zahl, Einheit).
 * „1,5 l" liest sich besser als „1500 ml" – aber nur, solange die große
 * Einheit ohne Nachkomma-Rauschen auskommt (1333 ml bleiben 1333 ml).
 */
export function fromBaseSize(
  value: number | null | undefined,
  unit: string | null | undefined,
): { value: number | ''; unit: string } {
  if (value === null || value === undefined || !unit) return { value: '', unit: '' };
  const bigger: Record<string, string> = { ml: 'l', g: 'kg' };
  const target = bigger[unit];
  if (target && value >= 1000) {
    const scaled = value / 1000;
    if (Math.abs(scaled * 100 - Math.round(scaled * 100)) < 1e-9) {
      return { value: Math.round(scaled * 100) / 100, unit: target };
    }
  }
  return { value, unit };
}
